import styles from './vacancy-form.module.scss';
import { Form, Formik, FormikHelpers } from 'formik';
import { string as yupString, object as yupObject, array as yupArray } from 'yup';
import Image from 'next/image';
import { ISpecialitiesItem } from '@components/careers/vacancies/vacancies.interfaces';
import Checkbox from '@components/shared/checkbox/checkbox';
import { useState } from 'react';
import UploadedFileComponent from '@components/shared/miscellaneous/uploaded-file-component/uploaded-file';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Grecaptcha from '@libs/integrations/grecaptcha';
import { sendApplication } from '@services/application.service';
import SocialLinkComponent from '@components/shared/miscellaneous/social-link-component/social-link-component';
import { ButtonHandshkaeGreen } from '@components/shared/buttons/button-handshake-green';

interface IVacancyFormProps {
  specialities: ISpecialitiesItem[];
  specialitiesTotal: number;
  isOpen: boolean;
  onSubmit: (success: boolean) => void;
  currentFormStatus: string;
}

interface FormValues {
  name: string;
  email: string;
  message: string;
  phone: string;
  specialty: string[];
}

const VacancyForm = ({ specialities, specialitiesTotal, isOpen, onSubmit, currentFormStatus }: IVacancyFormProps) => {
  const [id, setId] = useState<string | null>(null);
  const linkRegex = /(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;
  let [uploadedFiles, setUploadedFiles]: any[] = useState([]);
  const [phone, setPhone]: any = useState('');
  const [specialtyArray, addNewSpecialty]: any[] = useState([]);
  const [isFormSubmitted, setFormSubmitted] = useState(false);
  const [linksArray, addLinkToArray]: any = useState([]);
  const [link, setLink]: any = useState();
  const [linkValidationError, setLinkValidationError] = useState('');
  const [defaultPhoneCountry, setDefaultPhoneCountry] = useState('us');

  function uploadFile() {
    let fileInput: any = document.getElementById('file-upload');
    let files: any[] = [...fileInput.files];
    setUploadedFiles([...uploadedFiles, ...files]);
  }

  function deleteFile(name: string) {
    setUploadedFiles(uploadedFiles.filter((item: any) => item.name !== name));
  }

  function addLinkOnPressDown(event: any) {
    if (event.key === 'Enter') {
      event.preventDefault();
      addLink(link);
    }
  }

  function addLink(link: string) {
    if (!linkRegex.test(link)) {
      setLinkValidationError('Please enter the correct link address');
    } else {
      addLinkToArray([...linksArray, link]);
      setLink('');
    }
    if (link === '' && linksArray.length > 0) {
      setLinkValidationError('');
    }
  }

  function deleteLink(link: string) {
    addLinkToArray(linksArray.filter((item: string) => item !== link));
  }

  function addSpecialty(id: string) {
    addNewSpecialty([...specialtyArray, id]);
  }

  function deleteSpecialty(id: string) {
    addNewSpecialty(specialtyArray.filter((item: any) => item !== id));
  }

  return (
    <>
      <Grecaptcha />
      <Formik
        initialValues={{ name: '', email: '', message: '', phone: '', specialty: specialtyArray }}
        values={{ specialty: specialtyArray }}
        validationSchema={yupObject({
          name: yupString().required('Please enter your name'),
          email: yupString().required('Please enter your email address').email('Please enter a valid email address'),
          message: yupString(),
          phone: yupString(),
          specialty: yupArray(yupString()).min(1, `Please choose at least one specialty`),
        })}
        onSubmit={(values: FormValues, actions: FormikHelpers<FormValues>) => {
          setFormSubmitted(true);
          actions.setSubmitting(true);
          document.getElementById('preloader')?.scrollIntoView();
          actions.setFieldValue('specialty', []);
          Promise.all([
            sendApplication(
              values.name,
              values.email,
              values.message,
              phone,
              linksArray,
              specialtyArray,
              uploadedFiles,
              ``,
            ),
            new Promise((resolve) => {
              setTimeout(resolve, 500);
            }),
          ])
            .then(() => {
              actions.setSubmitting(false);
              setFormSubmitted(false);
              onSubmit(true);
            })
            .catch(() => {
              onSubmit(false);
              setFormSubmitted(false);
              actions.setSubmitting(false);
            });
          actions.resetForm();
          setPhone('');
          setUploadedFiles([]);
          addNewSpecialty([]);
        }}
      >
        {(formik) => (
          <Form
            className={`${isOpen && !isFormSubmitted && currentFormStatus === 'active' ? styles.detail__form__block : styles.detail__form__block_hidden}`}
            action=""
            noValidate
          >
            <label htmlFor={`name_${id}`} className={styles.detail__form__item}>
              <div className={styles.input__title}>Full Name</div>
              <div className={styles.detail__form__block__input}>
                <input
                  {...formik.getFieldProps('name')}
                  placeholder="Your Name"
                  className={`${styles.detail__form__input} ${formik.errors.name && formik.touched.name && styles.detail__form__input_invalid}`}
                  type="text"
                  id={`name_${id}`}
                />
              </div>
              {formik.errors.name && formik.touched.name ? (
                <div className={styles.subscribe__error_message}>{formik.errors.name}</div>
              ) : null}
            </label>

            <label htmlFor={`email_${id}`} className={styles.detail__form__item}>
              <div className={styles.input__title}>Email</div>
              <div className={styles.detail__form__block__input}>
                <input
                  {...formik.getFieldProps('email')}
                  placeholder="your@email.com"
                  className={`${styles.detail__form__input} ${formik.errors.email && formik.touched.email && styles.detail__form__input_invalid}`}
                  type="email"
                  id={`email_${id}`}
                />
              </div>
              {formik.errors.email && formik.touched.email ? (
                <div className={styles.subscribe__error_message}>{formik.errors.email}</div>
              ) : null}
            </label>

            <label htmlFor={`phone_${id}`} className={styles.detail__form__item}>
              <div className={styles.input__title}>Phone number (optional)</div>
              <div className={styles.detail__form__block__input}>
                <PhoneInput
                  country={defaultPhoneCountry}
                  value={phone}
                  onChange={(phone) => setPhone(phone)}
                  inputClass={'detail__form__input'}
                  buttonClass={'detail__form__btn'}
                  dropdownClass={'detail__form__dropdown'}
                />
              </div>
              {formik.errors.phone && formik.touched.phone ? (
                <div className={styles.subscribe__error_message}>{formik.errors.phone}</div>
              ) : null}
            </label>

            <label htmlFor={`links_${id}`} className={styles.detail__form__item}>
              <div className={styles.input__title}>LinkedIn profile, Github, personal website or portfolio</div>
              <div className={styles.detail__form__block__input}>
                <input
                  {...formik.getFieldProps('links')}
                  placeholder="Your link"
                  className={`${styles.detail__form__input} ${linkValidationError !== '' && styles.detail__form__input_invalid}`}
                  type="text"
                  id={`links_${id}`}
                  value={link}
                  onKeyDown={addLinkOnPressDown}
                  onChange={(e) => {
                    setLink(e.target.value);
                    if (!linkRegex.test(e.target.value)) {
                      setLinkValidationError('Please enter the correct link address');
                      return;
                    } else {
                      setLinkValidationError('');
                    }
                    if (linksArray.length > 0 && e.target.value === ``) {
                      setLinkValidationError('');
                    }
                  }}
                />
              </div>
              {linkValidationError !== '' ? (
                <div className={styles.subscribe__error_message}>{linkValidationError}</div>
              ) : null}
            </label>
            {linksArray.map((item: string, index: number) => {
              return <SocialLinkComponent key={item + index} link={item} deleteLink={() => deleteLink(item)} />;
            })}
            <label htmlFor={`specialties_${id}`} className={styles.form__block__item}>
              <div className={styles.input__title}>Please, notify me about:</div>
              <div className={`${styles.checkbox}`}>
                {specialities.map((item: ISpecialitiesItem, index: number) => {
                  return (
                    <Checkbox
                      key={index}
                      title={item.title}
                      addSpecialty={() => {
                        formik.setFieldValue('specialty', [...formik.values.specialty, item.id]);
                        addSpecialty(item.id);
                      }}
                      deleteSpecialty={() => {
                        formik.setFieldValue('specialty', [
                          ...formik.values.specialty.filter((value: any) => value !== item.id),
                        ]);
                        deleteSpecialty(item.id);
                      }}
                      isFormSubmitted={isFormSubmitted}
                    />
                  );
                })}
              </div>
              {formik.errors.specialty && formik.touched.specialty ? (
                <div className={styles.subscribe__error_message}>{formik.errors.specialty}</div>
              ) : null}
            </label>
            <label htmlFor={`message_${id}`} className={styles.detail__form__item}>
              <div className={styles.input__title}>Cover letter message (optional)</div>
              <div className={styles.detail__form__block__input}>
                <textarea
                  {...formik.getFieldProps('message')}
                  placeholder="Your message"
                  className={`${styles.detail__form__input} ${styles.detail__form__input_message} ${formik.errors.message && formik.touched.message && styles.detail__form__input_invalid}`}
                  id={`message_${id}`}
                />
              </div>
              {formik.errors.message && formik.touched.message ? (
                <div className={styles.subscribe__error_message}>{formik.errors.message}</div>
              ) : null}
            </label>
            <div className={`${styles.form__block__item} ${styles.form__block__item_flex}`}>
              <input
                {...formik.getFieldProps('files')}
                onChange={() => uploadFile()}
                type="file"
                id={'file-upload'}
                className={`${styles.form__input_file}`}
                multiple
                accept="application/pdf, text/plain, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              />
              <label htmlFor={'file-upload'} className={`${styles.form__button_dnd}`}>
                <div className={`${styles.form__button_dnd__image}`}>
                  <Image alt="attach" src={'/vacancies-images/icons/icn_attach.png'} width={'24px'} height={'24px'} />
                </div>
                <div>Attach a file</div>
              </label>
              <div className={styles.form__block__item__text}>
                <div className={styles.form__block__item__text_second}>Accepted File Types:</div>
                <div className={styles.form__block__item__text_black}>.pdf, .doc[x], .xls[x], .txt</div>
              </div>
            </div>
            <div id="file-upload-place">
              {uploadedFiles.map((item: any, index: number) => {
                return (
                  <UploadedFileComponent
                    key={index}
                    name={item.name}
                    size={item.size}
                    deleteFile={() => deleteFile(item.name)}
                  />
                );
              })}
            </div>
            <ButtonHandshkaeGreen text="Submit application" action={formik.submitForm} />
          </Form>
        )}
      </Formik>
      <div
        id="preloader"
        className={`${styles.form__preloader} ${isFormSubmitted ? styles.form__preloader_active : styles.form__preloader_hidden}`}
      >
        <span className="spinner spinner-large spinner-blue spinner-slow"></span>
      </div>
    </>
  );
};

export default VacancyForm;
